/**
 * RealWaitlistTab
 * ──────────────────────────────────────────────────────────────────────
 * Real-mode wrapper around `<WaitlistByRole />` that fetches the
 * waitlist + analytics from the AriaOne API and binds the kanban
 * actions back to the corresponding write endpoints.
 *
 * Endpoints consumed (all `/api/scheduler/waitlist*`, vendor §M9l):
 *   GET    /waitlist?location_id&status         → patients[]
 *   GET    /waitlist/analytics?location_id      → analytics{}
 *   PATCH  /waitlist/{id}/status                → status update
 *   DELETE /waitlist/{id}                       → remove
 *
 * NO new API shapes invented — this component consumes the existing
 * helpers in `lib/api/schedulerApi.js`. When the backend ships A16–A20
 * the live data flows in unchanged. Until then the GET calls 404 and
 * we surface a graceful empty/error state — no mock data leaks.
 */
import { useState, useEffect, useCallback } from 'react';
import { Card, CardContent } from '../../../ui/card';
import { Button } from '../../../ui/button';
import { Badge } from '../../../ui/badge';
import { toast } from 'sonner';
import { Users, AlertCircle, RefreshCw, Loader2 } from 'lucide-react';
import * as schedulerApi from '../../../../lib/api/schedulerApi';
import WaitlistByRole from './WaitlistByRole';
import { useReportSchedulerSource } from '../../../../lib/scheduler/SchedulerTraceContext';

const toastApiError = (err, fallback = 'Action failed.') => {
  const status = err?.status;
  const msg = err?.message || fallback;
  if (status === 403) {
    toast.error('Permission denied — your role cannot perform this action.', { description: msg });
  } else if (status === 409) {
    toast.error('Conflict — please refresh and retry.', { description: msg });
  } else if (status === 422) {
    toast.error('Cannot apply this action.', { description: msg });
  } else if (status === 404) {
    toast.error('Waitlist entry not found.', { description: msg });
  } else if (status && status >= 500) {
    toast.error('Server error — please retry.', { description: msg });
  } else {
    toast.error(fallback, { description: msg });
  }
};

const RealWaitlistTab = ({ role, locationId, onAdd }) => {
  const [patients, setPatients] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [pending, setPending] = useState(() => new Set());

  const refetch = useCallback(async () => {
    setLoading(true);
    setError(null);
    const wRes = await schedulerApi.listWaitlist(
      locationId ? { location_id: locationId } : undefined,
    );
    if (!wRes?.ok) {
      setError(wRes?.error || { message: 'Failed to load waitlist.' });
      setPatients([]);
    } else {
      const items = Array.isArray(wRes.data?.items) ? wRes.data.items
                  : Array.isArray(wRes.data?.waitlist) ? wRes.data.waitlist
                  : Array.isArray(wRes.data?.patients) ? wRes.data.patients
                  : [];
      // Sort by probability desc when present, else preserve API order.
      const sorted = items.slice().sort((a, b) =>
        (b.probability ?? 0) - (a.probability ?? 0),
      );
      setPatients(sorted.map((p, i) => ({ ...p, rank: p.rank ?? i + 1 })));
    }
    if (role === 'manager' || role === 'admin' || role === 'super_admin') {
      const aRes = await schedulerApi.getWaitlistAnalytics(
        locationId ? { location_id: locationId } : undefined,
      );
      if (aRes?.ok && aRes.data) {
        // Carry through only the fields ManagerView consumes.
        setAnalytics({
          totalToday:    aRes.data.total_today    ?? aRes.data.totalToday    ?? null,
          notified:      aRes.data.notified       ?? null,
          booked:        aRes.data.booked         ?? null,
          declined:      aRes.data.declined       ?? null,
          stillWaiting:  aRes.data.still_waiting  ?? aRes.data.stillWaiting  ?? null,
          avgWaitDays:   aRes.data.avg_wait_days  ?? aRes.data.avgWaitDays   ?? null,
        });
      } else {
        setAnalytics(null);
      }
    }
    setLoading(false);
  }, [locationId, role]);

  useEffect(() => { refetch(); }, [refetch]);

  /* In-flight guard — prevents double-fire on rapid clicks */
  const guard = useCallback((id) => {
    if (!id) return false;
    if (pending.has(id)) return false;
    setPending((prev) => { const next = new Set(prev); next.add(id); return next; });
    return true;
  }, [pending]);
  const release = useCallback((id) => {
    setPending((prev) => {
      if (!prev.has(id)) return prev;
      const next = new Set(prev); next.delete(id); return next;
    });
  }, []);

  const handleNotify = useCallback(async (p) => {
    if (!p?.id || !guard(p.id)) return;
    try {
      const res = await schedulerApi.updateWaitlistStatus(p.id, { status: 'notified' });
      if (!res?.ok) { toastApiError(res?.error, 'Could not notify patient.'); return; }
      toast.success(`${p.name || 'Patient'} notified.`);
      await refetch();
    } finally { release(p.id); }
  }, [guard, release, refetch]);

  const handleBook = useCallback(async (p) => {
    if (!p?.id || !guard(p.id)) return;
    try {
      const res = await schedulerApi.updateWaitlistStatus(p.id, { status: 'booked' });
      if (!res?.ok) { toastApiError(res?.error, 'Could not mark as booked.'); return; }
      toast.success(`${p.name || 'Patient'} marked as booked.`);
      await refetch();
    } finally { release(p.id); }
  }, [guard, release, refetch]);

  const handleRemove = useCallback(async (p) => {
    if (!p?.id || !guard(p.id)) return;
    try {
      const res = await schedulerApi.removeFromWaitlist(p.id);
      if (!res?.ok) { toastApiError(res?.error, 'Could not remove from waitlist.'); return; }
      toast.info(`${p.name || 'Patient'} removed.`);
      await refetch();
    } finally { release(p.id); }
  }, [guard, release, refetch]);

  /* Approval queue actions (manager view). The vendor doesn't expose a
   * dedicated approve/decline endpoint yet — we treat them as
   * status updates against the same waitlist row to stay within
   * existing API shapes. When the dedicated routes ship, swap the
   * helper without touching this component's contract. */
  const handleApprove = useCallback(async (a) => {
    if (!a?.id || !guard(a.id)) return;
    try {
      const res = await schedulerApi.updateWaitlistStatus(a.id, { status: 'approved' });
      if (!res?.ok) { toastApiError(res?.error, 'Could not approve.'); return; }
      toast.success(`Approved · ${a.patientName || a.name || 'patient'}.`);
      await refetch();
    } finally { release(a.id); }
  }, [guard, release, refetch]);
  const handleDecline = useCallback(async (a) => {
    if (!a?.id || !guard(a.id)) return;
    try {
      const res = await schedulerApi.updateWaitlistStatus(a.id, { status: 'declined' });
      if (!res?.ok) { toastApiError(res?.error, 'Could not decline.'); return; }
      toast.info(`Declined · ${a.patientName || a.name || 'patient'} stays on waitlist.`);
      await refetch();
    } finally { release(a.id); }
  }, [guard, release, refetch]);

  /* ─── render ─── */

  // Source state BEFORE early returns (rules of hooks).
  const waitlistSource =
    loading                   ? 'loading'
    : error?.status === 403   ? 'forbidden'
    : error?.status === 404   ? 'api_missing'
    : error                   ? 'error'
    : patients.length > 0     ? 'live'
    : 'empty';
  useReportSchedulerSource('waitlist', waitlistSource);

  if (error) {
    const status = error.status;
    if (status === 403) {
      return (
        <Card className="border-amber-200 bg-amber-50/40" data-testid="waitlist-403-card" data-waitlist-source="forbidden">
          <CardContent className="p-4 flex items-center gap-3">
            <AlertCircle className="h-4 w-4 text-amber-500" />
            <p className="text-[11px] text-amber-700">
              Your role does not have permission to view the waitlist.
            </p>
          </CardContent>
        </Card>
      );
    }
    if (status === 404) {
      // Not yet provisioned — graceful empty state. No mock data.
      return (
        <Card className="border-dashed border-slate-200" data-testid="waitlist-empty-real" data-waitlist-source="api_missing">
          <CardContent className="p-6 text-center">
            <Users className="h-5 w-5 text-slate-300 mx-auto mb-1" />
            <p className="text-[11px] text-slate-500">
              Waitlist is empty in real mode. Entries will appear here once the waitlist API is provisioned for this tenant.
            </p>
          </CardContent>
        </Card>
      );
    }
    return (
      <Card className="border-rose-200 bg-rose-50/40" data-testid="waitlist-error-card" data-waitlist-source="error">
        <CardContent className="p-4 flex items-center justify-between gap-3">
          <div className="flex items-center gap-2 text-[11px] text-rose-700">
            <AlertCircle className="h-4 w-4" />
            <span>{error.message || 'Failed to load waitlist.'}</span>
          </div>
          <Button variant="outline" size="sm" onClick={refetch} data-testid="waitlist-retry-btn">
            <RefreshCw className="h-3.5 w-3.5 mr-1" /> Retry
          </Button>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-3" data-testid="waitlist-real" data-waitlist-source={waitlistSource}>
      {/* Header strip — active count is derived from the same list the
          ranked cards render below, so the number is always exact. */}
      <div className="flex items-center justify-between text-[11px] text-slate-600" data-testid="waitlist-header-strip">
        <div className="inline-flex items-center gap-2">
          <Users className="h-3 w-3 text-slate-400" />
          <span>
            <span className="font-semibold text-slate-800 tabular-nums" data-testid="waitlist-active-count">{patients.length}</span> active waitlist {patients.length === 1 ? 'entry' : 'entries'}
          </span>
          {analytics?.best_match_count != null && (
            <Badge className="text-[9px] bg-emerald-50 text-emerald-700 border border-emerald-200">
              {analytics.best_match_count} best-slot match
            </Badge>
          )}
        </div>
        <button
          type="button"
          onClick={refetch}
          className="text-[10px] text-slate-500 hover:text-slate-800 inline-flex items-center gap-1"
          style={{ background: 'none', border: 'none', cursor: 'pointer' }}
          data-testid="waitlist-refresh-btn"
          title="Refresh waitlist + analytics"
        >
          <RefreshCw className="h-3 w-3" /> Refresh
        </button>
      </div>

      {loading && (
        <div className="text-[11px] text-slate-500 flex items-center gap-1.5" data-testid="waitlist-loading">
          <Loader2 className="h-3 w-3 animate-spin" />
          Loading waitlist…
        </div>
      )}
      {!loading && patients.length === 0 && (
        <Card className="border-dashed border-slate-200" data-testid="waitlist-empty-real">
          <CardContent className="p-5 text-center">
            <Users className="h-5 w-5 text-slate-300 mx-auto mb-1.5" />
            <p className="text-[12px] font-medium text-slate-700">Nobody waiting</p>
            <p className="text-[10px] text-slate-500 mt-0.5">
              {locationId
                ? 'Patients added to the waitlist for this location will appear here ranked by priority and best-slot match.'
                : 'Add a patient to the waitlist using the button above — they\'ll be ranked when capacity opens up.'}
            </p>
            {onAdd && (
              <Button variant="outline" size="sm" className="h-7 text-[10px] gap-1 mt-2" onClick={onAdd} data-testid="waitlist-empty-add-btn">
                Add to waitlist
              </Button>
            )}
          </CardContent>
        </Card>
      )}
      <WaitlistByRole
        role={role}
        patients={patients}
        pendingApprovals={[]}
        analytics={analytics}
        onAdd={onAdd}
        onNotify={handleNotify}
        onBook={handleBook}
        onRemove={handleRemove}
        onApprove={handleApprove}
        onDecline={handleDecline}
      />
    </div>
  );
};

export default RealWaitlistTab;
